import React from 'react';
import { ArrowUpRight } from 'lucide-react';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_80a9eaca-abc5-4772-88de-8f55ddf92961/artifacts/h9564xbj_Screenshot%202026-07-14%20at%204.51.31%E2%80%AFPM.png';

/**
 * V2C — The Executive Briefing
 * Editorial serif, cream background, laptop mockup, hotel-brand logo cloud.
 */
export default function LandingV2C({ onGoToLogin, onGoToSignup }) {
  return (
    <div className="min-h-screen" style={{ background: '#F5F5F0', fontFamily: 'IBM Plex Sans, system-ui, sans-serif', color: '#0A0A0A' }}>
      {/* Nav */}
      <nav className="border-b border-black/10" data-testid="v2c-nav">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-2.5 w-2.5 rounded-full" style={{ background: '#4C1D95' }} />
            <span className="text-lg tracking-tight" style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>LodgiFlow</span>
          </div>
          <div className="hidden md:flex items-center gap-10 text-[13px] uppercase tracking-[0.12em] text-black/60">
            <a href="#platform" className="hover:text-black transition-colors">Platform</a>
            <a href="#leadership" className="hover:text-black transition-colors">Leadership</a>
            <a href="#insights" className="hover:text-black transition-colors">Insights</a>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={onGoToLogin} className="text-[13px] uppercase tracking-[0.12em] text-black/70 hover:text-black" data-testid="v2c-login">Log In</button>
            <button onClick={onGoToSignup} className="px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.15em] bg-black text-white hover:bg-black/85 transition-colors" data-testid="v2c-cta-nav">Executive Briefing</button>
          </div>
        </div>
      </nav>

      {/* Hero — editorial centered */}
      <section className="max-w-5xl mx-auto px-6 pt-24 pb-16 text-center">
        <div className="text-[11px] font-medium uppercase tracking-[0.25em] text-purple-900 mb-8" data-testid="v2c-eyebrow">
          Trusted by 30+ multi-property organizations
        </div>
        <h1 style={{ fontFamily: 'Cormorant Garamond', fontWeight: 500, fontSize: 'clamp(3rem, 8vw, 6.5rem)', lineHeight: 1.02, letterSpacing: '-0.02em' }} data-testid="v2c-headline">
          Technology leadership,<br />
          <em style={{ fontStyle: 'italic', color: '#4C1D95' }}>simplified.</em>
        </h1>
        <p className="mt-8 text-lg text-black/70 max-w-2xl mx-auto leading-[1.7]">
          LodgiFlow is the enterprise platform for hospitality technology teams who refuse to manage a <span className="italic">$24M tech portfolio</span> in spreadsheets.
        </p>
        <div className="mt-10 flex items-center justify-center gap-8">
          <button onClick={onGoToSignup} className="px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] bg-black text-white hover:bg-black/85 transition-colors" data-testid="v2c-cta-primary">
            Request Executive Briefing
          </button>
          <button onClick={onGoToLogin} className="group inline-flex items-center gap-2 text-sm font-medium text-purple-900 border-b border-purple-900/40 hover:border-purple-900 pb-1 transition-colors" data-testid="v2c-cta-secondary">
            Read the Case Study
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </section>

      {/* Laptop mockup rising */}
      <section className="max-w-6xl mx-auto px-6 pb-24 relative">
        <div className="relative rounded-lg overflow-hidden border border-black/10 mx-auto max-w-4xl transition-transform hover:-translate-y-1 duration-500" style={{ boxShadow: '0 40px 80px -20px rgba(0,0,0,0.25)' }}>
          <img src={HERO_IMG} alt="LodgiFlow Executive Dashboard" className="w-full h-auto block" />
        </div>
      </section>

      {/* Prestige logo cloud fold */}
      <section className="border-t border-black/10" style={{ background: '#EAEAE4' }}>
        <div className="max-w-6xl mx-auto px-6 py-20" data-testid="v2c-logo-cloud">
          <div className="text-center mb-14">
            <div className="text-[11px] font-medium uppercase tracking-[0.25em] text-black/50 mb-3">In good company</div>
            <h2 style={{ fontFamily: 'Cormorant Garamond', fontWeight: 400, fontSize: 'clamp(1.75rem,3vw,2.5rem)', letterSpacing: '-0.01em' }} className="italic">
              The operators reshaping hospitality technology.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 items-center opacity-60">
            {['OMNI', 'LOEWS', 'HIGHGATE', 'KIMPTON', 'SONESTA', 'PENDRY'].map((brand) => (
              <div key={brand} className="text-center transition-opacity hover:opacity-100" style={{ fontFamily: 'Cormorant Garamond', fontWeight: 500, fontSize: '1.35rem', letterSpacing: '0.15em', color: '#0A0A0A' }} data-testid={`v2c-logo-${brand.toLowerCase()}`}>
                {brand}
              </div>
            ))}
          </div>

          {/* Editorial pull-quote */}
          <div className="mt-20 max-w-3xl mx-auto text-center">
            <div style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: 'clamp(1.5rem,2.5vw,2rem)', lineHeight: 1.4, letterSpacing: '-0.005em' }}>
              &ldquo;For the first time, I have a single view of every platform, contract, and integration across our 47 properties. It's the difference between managing tech and leading it.&rdquo;
            </div>
            <div className="mt-6 flex items-center justify-center gap-3 text-sm">
              <div className="h-10 w-10 rounded-full bg-purple-900 flex items-center justify-center text-white font-medium">JT</div>
              <div className="text-left">
                <div className="font-medium">Jonathan Therron</div>
                <div className="text-black/50 text-[12px] uppercase tracking-wider">CIO, Innisfree Hospitality</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
